import React, { useReducer, useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import {
  Button,
  Card,
  Form,
  FormLayout,
  TextField,
  Link as PolarisLink,
} from '@shopify/polaris';
import { validateFields } from 'src/utils/validations';
import SignInMutation from 'src/mutations/SignInMutation';
import styleConstants from 'src/styles/constants';
import PropTypes from 'prop-types';
import retrieveSubdomain from 'src/utils/retrieveSubdomain';
import { RECOVERY_PASSWORD_URL } from './constants';
import validations from './validations';

const initialState = {
  email: '',
  password: '',
  domain: '',
};

const reducer = (state, action) => {
  const { key, value } = action;
  return {
    ...state,
    [key]: value,
  };
};

const SignInComponent = ({ onError, siteOwner, shop, onSuccess }) => {
  const [signIn, { loading }] = useMutation(SignInMutation);
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    email: siteOwner.email,
  });
  const { email, password, domain } = state;
  const [validationErrors, setValidationErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitErrors = validateFields(state, validations);
    if (Object.keys(submitErrors).length) {
      return setValidationErrors(submitErrors);
    }

    const variables = {
      email,
      password,
      shop,
      subdomain: retrieveSubdomain(domain),
    };

    try {
      const { data } = await signIn({ variables });
      if (data && !data.signIn.result.success) {
        const { errors } = data.signIn.result;
        if (errors.length > 1) {
          return onError({
            title: 'Sign in unsuccessful.',
            message: errors.map(({ message }) => message),
          });
        }
        return onError(errors[0]);
      }
      const { tenant, ...session } = data.signIn.session;
      return onSuccess(session, tenant);
    } catch (err) {
      const error = {
        title: 'Sign in unsuccessful.',
        message: err.message,
      };
      return onError(error);
    }
  };

  useEffect(() => {
    if (siteOwner) {
      dispatch({ key: 'email', value: siteOwner.email });
    }
  }, [siteOwner]);

  return (
    <>
      <Card>
        <Card.Section>
          <Form onSubmit={handleSubmit}>
            <FormLayout>
              <TextField
                error={
                  validationErrors.domain && (
                    <>
                      {validationErrors.domain.required && (
                        <span>{validationErrors.domain.required}</span>
                      )}
                      {!validationErrors.domain.required &&
                        validationErrors.domain.domain && (
                          <span>{validationErrors.domain.domain}</span>
                        )}
                    </>
                  )
                }
                helpText="Custom domains will not be recognized."
                label="Thinkific site URL"
                type="text"
                value={domain}
                onChange={(value) => dispatch({ key: 'domain', value })}
              />
              <TextField
                error={
                  validationErrors.email && (
                    <>
                      {validationErrors.email.required && (
                        <span>{validationErrors.email.required}</span>
                      )}
                      {!validationErrors.email.required &&
                        validationErrors.email.email && (
                          <span>{validationErrors.email.email}</span>
                        )}
                    </>
                  )
                }
                label="Thinkific site owner email"
                type="text"
                value={email}
                no-validate
                onChange={(value) => dispatch({ key: 'email', value })}
              />

              <TextField
                error={
                  validationErrors.password && (
                    <>
                      {validationErrors.password.required && (
                        <span>{validationErrors.password.required}</span>
                      )}
                    </>
                  )
                }
                label="Password"
                type="password"
                value={password}
                onChange={(value) => dispatch({ key: 'password', value })}
              />
            </FormLayout>
            <div className="signin__actions">
              <PolarisLink url={RECOVERY_PASSWORD_URL} external>
                Forgot password?
              </PolarisLink>
              <Button loading={loading} primary submit>
                Connect site
              </Button>
            </div>
          </Form>
        </Card.Section>
      </Card>

      <style jsx>{`
        .signin__actions {
          display: flex;
          justify-content: flex-end;
          flex-direction: column;
        }
        :global(.Polaris-Link) {
          padding: ${styleConstants.spacing.two} 0
            ${styleConstants.spacing.four};
        }
      `}</style>
    </>
  );
};

SignInComponent.propTypes = {
  shop: PropTypes.string.isRequired,
  siteOwner: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
export default SignInComponent;
